import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save,
  RestartAlt,
  Add,
  Delete,
  Edit,
  Notifications,
  Security,
  Wifi,
  Storage,
  DeviceHub,
  Person,
  Tune,
  Close,
} from '@mui/icons-material';

interface User {
  id: string;
  userId: string;
  name: string;
  role: 'ADMIN' | 'MANAGER' | 'USER';
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  email?: string;
  phone?: string;
  createdAt: string;
}

interface SystemSetting {
  id: string;
  key: string;
  value: string;
  dataType: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON';
  description: string;
}

const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [saveAlert, setSaveAlert] = useState(false);
  const [userDialog, setUserDialog] = useState({ open: false, user: null as User | null });

  // 농장 설정
  const [farmSettings, setFarmSettings] = useState({
    farmName: 'SMARTFARM 01',
    location: '경기도 김포시',
    description: '스마트팜 시설재배',
    timezone: 'Asia/Seoul',
    language: 'ko',
  });

  // 센서 임계값 설정
  const [sensorThresholds, setSensorThresholds] = useState({
    temperature: { min: 18, max: 28, unit: '°C' },
    humidity: { min: 50, max: 70, unit: '%' },
    co2: { min: 400, max: 800, unit: 'ppm' },
    light: { min: 10000, max: 20000, unit: 'lux' },
    soilMoisture: { min: 40, max: 60, unit: '%' },
    ec: { min: 1.0, max: 1.5, unit: 'mS/cm' },
  });

  // 알림 설정
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    criticalAlerts: true,
    maintenanceReminders: true,
    dataReports: false,
  });

  // 시스템 설정
  const [systemSettings, setSystemSettings] = useState<SystemSetting[]>([
    {
      id: '1',
      key: 'data_collection_interval',
      value: '5',
      dataType: 'NUMBER',
      description: '데이터 수집 간격 (분)',
    },
    {
      id: '2',
      key: 'auto_backup_enabled',
      value: 'true',
      dataType: 'BOOLEAN',
      description: '자동 백업 활성화',
    },
    {
      id: '3',
      key: 'max_log_retention_days',
      value: '30',
      dataType: 'NUMBER',
      description: '로그 보관 기간 (일)',
    },
    {
      id: '4',
      key: 'device_timeout_seconds',
      value: '60',
      dataType: 'NUMBER',
      description: '디바이스 타임아웃 (초)',
    },
  ]);

  // 사용자 관리
  const [users, setUsers] = useState<User[]>([
    {
      id: '1',
      userId: 'admin',
      name: '관리자',
      role: 'ADMIN',
      status: 'ACTIVE',
      email: 'admin@goodfarmer.com',
      phone: '010-1234-5678',
      createdAt: '2025.01.15',
    },
    {
      id: '2',
      userId: 'user123',
      name: 'User 123',
      role: 'USER',
      status: 'ACTIVE',
      email: 'user123@goodfarmer.com',
      phone: '010-9876-5432',
      createdAt: '2025.01.16',
    },
    {
      id: '3',
      userId: 'manager01',
      name: '농장 관리자',
      role: 'MANAGER',
      status: 'ACTIVE',
      email: 'manager@goodfarmer.com',
      createdAt: '2025.01.17',
    },
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = () => {
    setSaveAlert(true);
    setTimeout(() => setSaveAlert(false), 3000);
  };

  const handleThresholdChange = (sensor: string, type: 'min' | 'max', value: number) => {
    setSensorThresholds(prev => ({
      ...prev,
      [sensor]: {
        ...prev[sensor as keyof typeof prev],
        [type]: value,
      },
    }));
  };

  const getRoleChip = (role: string) => {
    const config = {
      ADMIN: { label: '관리자', color: 'error' as const },
      MANAGER: { label: '매니저', color: 'warning' as const },
      USER: { label: '사용자', color: 'info' as const },
    };
    const roleConfig = config[role as keyof typeof config];
    return <Chip label={roleConfig.label} color={roleConfig.color} size="small" />;
  };

  const getStatusChip = (status: string) => {
    const config = {
      ACTIVE: { label: '활성', color: 'success' as const },
      INACTIVE: { label: '비활성', color: 'default' as const },
      PENDING: { label: '대기중', color: 'warning' as const },
    };
    const statusConfig = config[status as keyof typeof config];
    return <Chip label={statusConfig.label} color={statusConfig.color} size="small" />;
  };

  return (
    <Box>
      {/* Save Alert */}
      {saveAlert && (
        <Alert severity="success" sx={{ mb: 2 }}>
          설정이 성공적으로 저장되었습니다.
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<Tune />} label="농장 설정" />
          <Tab icon={<DeviceHub />} label="센서 임계값" />
          <Tab icon={<Notifications />} label="알림 설정" />
          <Tab icon={<Person />} label="사용자 관리" />
          <Tab icon={<Storage />} label="시스템 설정" />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Farm Settings */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                  농장 기본 정보
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="농장명"
                      value={farmSettings.farmName}
                      onChange={(e) => setFarmSettings(prev => ({ ...prev, farmName: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="위치"
                      value={farmSettings.location}
                      onChange={(e) => setFarmSettings(prev => ({ ...prev, location: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="설명"
                      multiline
                      rows={3}
                      value={farmSettings.description}
                      onChange={(e) => setFarmSettings(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>시간대</InputLabel>
                      <Select
                        value={farmSettings.timezone}
                        onChange={(e) => setFarmSettings(prev => ({ ...prev, timezone: e.target.value }))}
                        label="시간대"
                      >
                        <MenuItem value="Asia/Seoul">아시아/서울 (KST)</MenuItem>
                        <MenuItem value="UTC">UTC</MenuItem>
                        <MenuItem value="America/New_York">미국/뉴욕 (EST)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>언어</InputLabel>
                      <Select
                        value={farmSettings.language}
                        onChange={(e) => setFarmSettings(prev => ({ ...prev, language: e.target.value }))}
                        label="언어"
                      >
                        <MenuItem value="ko">한국어</MenuItem>
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="ja">日本語</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    sx={{ borderRadius: 2 }}
                  >
                    저장
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RestartAlt />}
                    sx={{ borderRadius: 2 }}
                  >
                    초기화
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  농장 상태
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      등록일
                    </Typography>
                    <Typography variant="body1">
                      2025.01.15
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      센서 수
                    </Typography>
                    <Typography variant="body1">
                      6개 센서
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      디바이스 수
                    </Typography>
                    <Typography variant="body1">
                      4개 디바이스
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      사용자 수
                    </Typography>
                    <Typography variant="body1">
                      {users.filter(u => u.status === 'ACTIVE').length}명
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab Panel 1: Sensor Thresholds */}
      {tabValue === 1 && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              센서 임계값 설정
            </Typography>

            <Grid container spacing={3}>
              {Object.entries(sensorThresholds).map(([key, threshold]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {key === 'temperature' && '온도'}
                        {key === 'humidity' && '습도'}
                        {key === 'co2' && 'CO₂'}
                        {key === 'light' && '조도'}
                        {key === 'soilMoisture' && '토양수분'}
                        {key === 'ec' && 'EC'}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          ({threshold.unit})
                        </Typography>
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          최소값: {threshold.min}
                        </Typography>
                        <Slider
                          value={threshold.min}
                          onChange={(_, value) => handleThresholdChange(key, 'min', value as number)}
                          min={0}
                          max={key === 'light' ? 25000 : key === 'co2' ? 1000 : 100}
                          step={key === 'light' ? 1000 : key === 'co2' ? 50 : key === 'ec' ? 0.1 : 1}
                          valueLabelDisplay="auto"
                        />
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          최대값: {threshold.max}
                        </Typography>
                        <Slider
                          value={threshold.max}
                          onChange={(_, value) => handleThresholdChange(key, 'max', value as number)}
                          min={threshold.min}
                          max={key === 'light' ? 30000 : key === 'co2' ? 1200 : 100}
                          step={key === 'light' ? 1000 : key === 'co2' ? 50 : key === 'ec' ? 0.1 : 1}
                          valueLabelDisplay="auto"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ borderRadius: 2 }}
              >
                임계값 저장
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tab Panel 2: Notification Settings */}
      {tabValue === 2 && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              알림 설정
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  알림 방식
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.emailNotifications}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          emailNotifications: e.target.checked
                        }))}
                      />
                    }
                    label="이메일 알림"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.smsNotifications}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          smsNotifications: e.target.checked
                        }))}
                      />
                    }
                    label="SMS 알림"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.pushNotifications}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          pushNotifications: e.target.checked
                        }))}
                      />
                    }
                    label="푸시 알림"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  알림 유형
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.criticalAlerts}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          criticalAlerts: e.target.checked
                        }))}
                      />
                    }
                    label="위험 알림"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.maintenanceReminders}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          maintenanceReminders: e.target.checked
                        }))}
                      />
                    }
                    label="유지보수 알림"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.dataReports}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          dataReports: e.target.checked
                        }))}
                      />
                    }
                    label="데이터 리포트"
                  />
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ borderRadius: 2 }}
              >
                알림 설정 저장
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tab Panel 3: User Management */}
      {tabValue === 3 && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                사용자 관리
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ borderRadius: 2 }}
              >
                사용자 추가
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>사용자 ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>이름</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>역할</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>상태</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>연락처</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>등록일</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>작업</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {user.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getRoleChip(user.role)}
                      </TableCell>
                      <TableCell>
                        {getStatusChip(user.status)}
                      </TableCell>
                      <TableCell>
                        <Box>
                          {user.email && (
                            <Typography variant="caption" display="block">
                              {user.email}
                            </Typography>
                          )}
                          {user.phone && (
                            <Typography variant="caption" display="block">
                              {user.phone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.createdAt}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton size="small" color="primary">
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab Panel 4: System Settings */}
      {tabValue === 4 && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              시스템 설정
            </Typography>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>설정 키</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>값</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>타입</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>설명</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>작업</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systemSettings.map((setting) => (
                    <TableRow key={setting.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {setting.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {setting.value}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={setting.dataType}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {setting.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary">
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ borderRadius: 2 }}
              >
                시스템 설정 저장
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SettingsPage;